// InvoiceDetailPage.tsx — review one invoice: the reading beside the document
// (chunk 2, phase 4, built to the redline).
//
// The reading is a PROPOSAL, never the record: the original document renders
// beside it always, because that pairing is one of the three controls the
// reader lane's threat model names (the others: the confirm gate, the PO
// match). Confirm itself lands with phase 5 — it appears here disabled, with
// its reason printed, because a button that will exist and silently doesn't is
// worse than one that says when.
//
// Pin 3, as decided: an invoice with no resolved vendor holds confirm until a
// human picks one. Confirm rolls billing onto the matched orders and appends
// the invoiced event; it sends NOTHING — delivery is its own later transition,
// where pin 2's send toggle (default off for card-paid) lives.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { useToast } from '../components/Toast/ToastContext';
import BackLink from '../components/BackLink';
import HelpDrawer from '../components/HelpDrawer';
import { DocumentPane } from '../components/review/DocumentPane';
import { Segmented, FilterSelect } from '../components/FilterBar';
import { formatEventDate } from '../utils/date-utils';
import { openAuthedPdf } from '../Receiving/openAuthedPdf';
import EntityCombobox, { ComboboxOption } from '../components/EntityCombobox';
import PaymentNote from './PaymentNote';
import InvoiceLinesTable from './InvoiceLinesTable';
import PoReadOnlyPane from './PoReadOnlyPane';
import PageLayout from '../components/ui/PageLayout';
import { buttonClass } from '../components/ui/Button';
import Tag from '../components/ui/Tag';
import Alert, { type Tone } from '../components/ui/Alert';
import LogoSpinner from '../components/ui/LogoSpinner';
import { Panel } from '../components/ui/Panel';
import Tooltip from '../components/ui/Tooltip';
import Checkbox from '../components/ui/Checkbox';
import { Chip, ChipGroup } from '../components/ui/Chip';
import { TextArea } from '../components/ui/Field';
import Reveal from '../components/ui/Reveal';
import { bannerSentence } from './lineVerdict';
import {
  useApInvoicesApi, InvoiceDetail, RematchResult, RematchLine,
  VendorInvoiceLine, formatCents, AttentionReason, isOverdue,
  ClassificationOptions, addressLines, partyLines,
} from './apInvoicesApi';

const REASON_LABELS: Record<string, string> = {
  appears_card_paid: 'Paid by card already',
  po_vendor_mismatch: 'Order belongs to a different vendor',
  unknown_po_number: 'Names an order we do not hold',
  duplicate_file: 'Same file already in the system',
  duplicate_number: 'Same invoice number this period',
  vendor_disabled: 'Vendor is disabled',
  vendor_fuzzy_match: 'Vendor matched by containment',
  bills_beyond_received: 'Bills beyond what has arrived',
  return_pending: 'A return is still with the vendor',
  exceeds_remaining: 'A line exceeds what is left to bill',
  ambiguous_part: 'A part is open on several orders',
  ambiguous_price: 'A price matches several lines',
  line_already_billed: 'Points at a fully billed line',
  type_crossed_match: 'A charge is paired with a goods line',
  figures_dropped_currency: 'Foreign currency: figures not stored',
  remit_details_changed: 'Bank details differ from this vendor\'s last invoice',
};

const Field = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div>
    <div className="font-mono text-[10px] uppercase tracking-widest text-on-surface-variant mb-1">{label}</div>
    <div className="bg-surface-container-low rounded-md px-2.5 py-1.5 text-sm text-on-surface tabular-nums">{children}</div>
  </div>
);

type PaneTab = 'invoice' | 'order';


// Red holds an action, gold warns while letting the person through, green confirms.
const ALERT_TONE: Record<'red' | 'gold' | 'green', Tone> = { red: 'error', gold: 'warning', green: 'success' };

// Standing conditions on the document, so none interrupts a screen reader (live={false}).
const PageAlert = ({ tone, title, show = true, children }: { tone: 'red' | 'gold' | 'green'; title: string; show?: boolean; children?: React.ReactNode }) => (
  <Reveal show={show}>
    <Alert tone={ALERT_TONE[tone]} title={`${title}.`} live={false} className="mb-3">{children}</Alert>
  </Reveal>
);

export default function InvoiceDetailPage() {
  const { invoiceId } = useParams<{ invoiceId: string }>();
  const api = useApInvoicesApi();
  const history = useHistory();
  const toast = useToast();

  const [detail, setDetail] = useState<InvoiceDetail | null>(null);
  const [proposals, setProposals] = useState<RematchResult | null>(null);
  // One reason box, two verbs: discard leaves the review pile (never-was),
  // void tombstones a confirmed record (was-and-withdrawn).
  // The queue's D key arrives with { discard: true } in history state: the
  // reason box opens armed, so a discard stays two keys from handled.
  const location = useLocation<{ discard?: boolean } | undefined>();
  const [reasonMode, setReasonMode] = useState<null | 'discard' | 'void'>(
    location.state?.discard ? 'discard' : null,
  );
  const [reasonText, setReasonText] = useState('');
  // null = follow the default (on; off for card-paid). A click makes it explicit.
  const [sendChoice, setSendChoice] = useState<boolean | null>(null);
  const [busy, setBusy] = useState(false);
  // Its own flag rather than `busy`: opening a PDF writes nothing, so it must
  // not disable Confirm while it loads, and Confirm must not disable it.
  const [openingSummary, setOpeningSummary] = useState(false);
  const [options, setOptions] = useState<ClassificationOptions | null>(null);
  const [applyToAll, setApplyToAll] = useState(true);
  // null = follow the default: the paper, or the order when there is no paper.
  const [paneTab, setPaneTab] = useState<PaneTab | null>(null);
  const [orderPick, setOrderPick] = useState<string | null>(null);
  // The order pane mounts on first visit and then stays, so flipping tabs
  // does not refetch the order every time.
  const [orderSeen, setOrderSeen] = useState(false);

  // Two short static lists, fetched once and not with the invoice: they do not
  // change between rows, and a refetch on every navigation would flicker the
  // pickers for nothing.
  useEffect(() => {
    let cancelled = false;
    api.classificationOptions()
      .then((o) => { if (!cancelled) setOptions(o); })
      .catch(() => { /* the pickers simply do not render; the page still works */ });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = useCallback(async () => {
    const d = await api.getOne(invoiceId);
    setDetail(d);
    // Candidates come from a fresh, write-free rematch; stored links stay authority.
    api.rematch(invoiceId).then(setProposals).catch(() => setProposals(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceId]);

  useEffect(() => {
    // Drop the previous invoice's screen the moment the id changes: this
    // component stays mounted across /invoices/A → /invoices/B, and with a
    // mutating control on the page, a stale render is a wrong-invoice write
    // waiting for a click.
    setDetail(null);
    setProposals(null);
    setPaneTab(null);
    setOrderPick(null);
    setOrderSeen(false);
    load().catch((e: any) => toast.error(e?.message || 'That invoice could not be loaded.'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load]);

  // Referentially stable, so the combobox is not handed a fresh array every
  // render. The account name is the line you read; the number is the chip you
  // type, and both are matched when searching.
  const categoryOptions: ComboboxOption[] = useMemo(() => (
    (options?.categories || []).map((c) => ({
      id: c.id,
      label: c.qbo_account_name || c.label,
      sublabel: c.qbo_account_name && c.label !== c.qbo_account_name ? c.label : null,
      chip: c.qbo_account_ref,
    }))
  ), [options]);

  const proposalByLine = useMemo(() => {
    const map = new Map<number, RematchLine>();
    for (const l of proposals?.lines || []) map.set(l.line_number, l);
    return map;
  }, [proposals]);

  if (!detail) {
    return <PageLayout width="wide" kind="workspace" className="pt-6"><LogoSpinner label="Loading the invoice" /></PageLayout>;
  }
  const { invoice, lines } = detail;
  const reasons: AttentionReason[] = invoice.attention_reasons || [];
  const cardPaid = reasons.some((r) => r.code === 'appears_card_paid');
  const vendorUnknown = !invoice.vendor_id;
  const inReview = invoice.status === 'needs_review';
  // No readable USD total = a forward would file a bill with no amount; the
  // server refuses send:true for it, and the toggle says so instead of lying.
  const totalless = invoice.total_cents === null;
  const sendToBooks = totalless ? false : (sendChoice === null ? !cardPaid : sendChoice);
  // Whether this environment can send at all. False until QuickBooks is ready,
  // which is the whole pre-QBO period: the toggle then means "queue it", and
  // saying so is what stops a well-meaning OFF from creating an invoice no sweep
  // will ever forward.
  const sendingLive = detail.qbo_delivery_enabled !== false;
  // Confirmed + send intended + no delivered stamp splits in two: never
  // attempted = OWED (sweep or button sends it); attempted = outcome UNKNOWN
  // (a bill may already exist in QuickBooks — look before sending again).
  const undelivered = invoice.status === 'confirmed' && invoice.delivery_intended && !invoice.delivered_at;
  const deliveryOwed = undelivered && !invoice.delivery_attempted_at;
  const deliveryUnknown = undelivered && !!invoice.delivery_attempted_at;

  // The orders this invoice touches: what its lines are matched to, then (in
  // review) whatever the matcher proposes, first appearance first.
  const orderChoices: { id: string; po_number: string }[] = [];
  const seenOrders = new Set<string>();
  for (const l of lines) {
    const c = l.po_context;
    if (c && !seenOrders.has(c.purchase_order_id)) {
      seenOrders.add(c.purchase_order_id);
      orderChoices.push({ id: c.purchase_order_id, po_number: c.po_number });
    }
  }
  if (inReview) {
    for (const o of proposals?.orders || []) {
      if (!seenOrders.has(o.id)) {
        seenOrders.add(o.id);
        orderChoices.push({ id: o.id, po_number: o.po_number });
      }
    }
  }
  const selectedOrderId = orderChoices.find((o) => o.id === orderPick)?.id ?? orderChoices[0]?.id ?? null;
  const hasPaper = invoice.source !== 'manual';
  const tab: PaneTab = !selectedOrderId ? 'invoice' : (paneTab ?? (hasPaper ? 'invoice' : 'order'));
  const highlightLineIds = Array.from(new Set(
    lines
      .filter((l) => l.po_context && l.po_context.purchase_order_id === selectedOrderId)
      .map((l) => l.po_context!.purchase_order_line_id),
  ));

  const openSummaryPdf = async () => {
    setOpeningSummary(true);
    try {
      await openAuthedPdf(api.summaryPdfPath(invoice.id));
    } catch (e: any) {
      toast.error(e?.message || 'The summary could not be opened.');
    } finally { setOpeningSummary(false); }
  };

  const setClassification = async (patch: {
    purchase_class_id?: number | null; expense_category_id?: string | null;
  }) => {
    setBusy(true);
    try {
      const res = await api.setClassification(invoice.id, { ...patch, apply_to_all: applyToAll });
      setDetail((d) => (d ? { ...d, invoice: { ...d.invoice, ...res.invoice }, lines: res.lines } : d));
    } catch (e: any) {
      toast.error(e?.data?.error?.message || e?.message || 'That could not be saved.');
    } finally { setBusy(false); }
  };

  const saveVendorAddress = async () => {
    setBusy(true);
    try {
      const res = await api.adoptVendorAddress(invoice.id);
      toast.success(`Address saved to ${res.vendor.name}.`);
      await load();
    } catch (e: any) {
      toast.error(e?.data?.error?.message || e?.message || 'The address could not be saved.');
    } finally { setBusy(false); }
  };

  const pick = async (line: VendorInvoiceLine, target: string | null) => {
    setBusy(true);
    try {
      await api.setLineMatch(invoice.id, line.id, target);
      await load();
    } catch (e: any) {
      toast.error(e?.data?.error?.message || e?.message || 'That pick was refused.');
    } finally { setBusy(false); }
  };

  const confirm = async () => {
    // Never confirm without the paper: bring it back into view first.
    if (hasPaper) setPaneTab('invoice');
    setBusy(true);
    try {
      const res = await api.confirm(invoice.id, sendToBooks);
      const billing = res.affected_order_ids.length
        ? 'Billing rolled onto the matched order.'
        : 'No order lines were matched.';
      toast.success(sendToBooks
        ? (sendingLive
          ? `Confirmed. ${billing} The forward to QuickBooks follows.`
          : `Confirmed. ${billing} The forward is queued for when sending is switched on.`)
        : `Confirmed. ${billing} Nothing goes to QuickBooks.`);
      await load();
    } catch (e: any) {
      toast.error(e?.data?.error?.message || e?.message || 'Confirm was refused.');
    } finally { setBusy(false); }
  };

  const deliverNow = async () => {
    setBusy(true);
    try {
      const res = await api.deliver(invoice.id);
      if (res.delivered) toast.success('Forwarded to QuickBooks.');
      else if (res.stamp_failed) {
        toast.error('The mail went out, but the record could not be stamped. Check QuickBooks for the bill before any retry.');
      } else if (res.skipped) {
        toast.error(res.skipped === 'delivery_disabled'
          ? 'Sending is switched off in this environment. The forward stays owed.'
          : 'The mail transport is not configured here. The forward stays owed.');
      } else toast.error(`The send failed: ${res.error_message || 'unknown error'}.`);
      await load();
    } catch (e: any) {
      toast.error(e?.data?.error?.message || e?.message || 'The send was refused.');
    } finally { setBusy(false); }
  };

  const submitReason = async () => {
    setBusy(true);
    try {
      if (reasonMode === 'void') {
        await api.voidInvoice(invoice.id, reasonText);
        toast.success('Voided. The order\u2019s billing healed itself, and its feed says why.');
        setReasonMode(null);
        setReasonText('');
        await load();
      } else {
        await api.discard(invoice.id, reasonText);
        toast.success('Discarded, on record.');
        history.push('/invoices');
      }
    } catch (e: any) {
      toast.error(e?.data?.error?.message || e?.message || 'That was refused.');
    } finally { setBusy(false); }
  };

  return (
    <PageLayout width="wide" kind="record" className="pt-4 lg:pt-6">
      <BackLink to="/invoices" label="Invoices" />

      <div className="flex items-baseline gap-3 mt-2 mb-4 flex-wrap">
        <h1 className="text-2xl font-bold text-white">
          {invoice.invoice_number || 'Unnumbered invoice'}
        </h1>
        <HelpDrawer title="About this invoice">
          <p className="text-on-surface font-medium">Reading this page</p>
          <p>
            An assistant read this invoice and filled in the figures you see here. That
            reading is a proposal, never the record: the original document is shown beside
            it, and nothing is billed to a purchase order until somebody presses Confirm.
          </p>
          <p className="text-on-surface font-medium">Always read against the paper</p>
          <p>
            A misread is usually plausible, which is what makes it dangerous: a transposed
            total, a part number one character out. Check the figures against the document
            itself before you confirm. If the panel says the document could not be fetched,
            that is a reason to stop, not a display problem.
          </p>
          <p className="text-on-surface font-medium">What the banner colors mean</p>
          <p>
            Red holds an action: something has to be answered before this invoice can go
            through. Gold is a warning that still lets you through, so read it and decide.
            Green confirms something that already happened. Three colors, and no more, so
            you only learn them once.
          </p>
          <p className="text-on-surface font-medium">What Confirm does</p>
          <p>
            It rolls these amounts onto the purchase order lines matched below, and writes
            this invoice into each order&apos;s history. Where a line is not matched to an
            order, nothing rolls anywhere. With the send toggle on, a one-page summary is
            forwarded to QuickBooks afterwards; the forward is its own step, so confirming
            never waits on a mail server.
          </p>
          <p className="text-on-surface font-medium">What Confirm does not do</p>
          <p>
            It pays nobody and it sends nothing to the vendor. What goes to QuickBooks is a
            generated summary, never the original document, and nothing goes at all with the
            toggle off. It is also held while the vendor is unknown: a first document from a
            vendor we do not hold is the shape of a fake invoice, so a person picks or
            creates the vendor first.
          </p>
          <p className="text-on-surface font-medium">Credit memos</p>
          <p>
            A credit memo is read the same way and wears a chip beside its number. Its
            amounts are negative on purpose and print in blue, so confirming one reduces
            what an order has been billed instead of adding to it. The sign comes from the
            reading; there is nothing to flip here.
          </p>
          <p className="text-on-surface font-medium">Discard, and void</p>
          <p>
            Discard is for an invoice that should never have been in the pile: a duplicate,
            or something that is not ours. Void is for one that was already confirmed and
            has to be withdrawn, which heals the order&apos;s billing and says so in its
            history. Both ask why, and the next person reads that answer.
          </p>
        </HelpDrawer>
        <span className="text-on-surface-variant">{invoice.vendor_name || invoice.vendor_observed || 'Unknown vendor'}</span>
        {invoice.is_credit_memo && (
          <Tag tone="info" mono>CREDIT MEMO</Tag>
        )}
        <Tag variant="code" mono>{invoice.status}</Tag>
      </div>

      {/* An even split, and the paper is PINNED. The reading is long — banners,
          five header facts, the payment note, every line with its picker — and
          the document used to scroll away with it, so confirming happened from
          memory of a page last seen a screen and a half ago. Sticky at the top
          of the viewport, the paper is under the reviewer's eye for the whole
          scroll, which is the pairing this page exists to enforce.

          items-start, so the sticky child is not stretched to the row's height
          by the grid's default alignment — a stretched item has nothing left to
          scroll within and sticky silently does nothing. */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 items-start">
        <div>
          <PageAlert show={vendorUnknown && inReview} tone="red" title="Unknown vendor">
              A first document from an unknown vendor is also the shape of a fake invoice.
              Confirm stays held until a person picks or creates the vendor. Read
              "{invoice.vendor_observed || 'the document'}" against your vendor master.
          </PageAlert>
          {reasons.filter((r) => r.code !== 'appears_card_paid').map((r) => (
            <PageAlert key={r.code} tone={['po_vendor_mismatch', 'unknown_po_number', 'vendor_disabled'].includes(r.code) ? 'red' : 'gold'}
              title={REASON_LABELS[r.code] || r.code}>
              {bannerSentence(r.code, lines) ?? r.message}
            </PageAlert>
          ))}
          <PageAlert show={cardPaid} tone="gold" title="Paid by card already">
              {reasons.find((r) => r.code === 'appears_card_paid')?.message}{' '}
              The send toggle below defaults off for card-paid invoices; you choose each time.
          </PageAlert>
          <PageAlert show={invoice.status === 'delivered'} tone="green" title="Forwarded to QuickBooks">
              The summary went {invoice.delivered_at ? formatEventDate(invoice.delivered_at.slice(0, 10)) : ''}
              {invoice.delivery_ref ? ` (ref ${invoice.delivery_ref})` : ''}. One delivery, ever.
              Forwarded means the mail was accepted; while this pipeline is young, glance at
              QuickBooks' Bills list to see it landed.
          </PageAlert>
          <PageAlert show={deliveryUnknown} tone="red" title="A send attempt is on record, outcome unknown">
              The forward started and the result never came back, so the bill may already be in
              QuickBooks. Check its Bills list first; the retry door reopens fifteen minutes after
              the attempt.{' '}
              <button
                onClick={deliverNow}
                disabled={busy}
                data-testid="deliver-unknown-retry"
                className="underline text-red-300 hover:text-red-200 disabled:opacity-50"
              >
                Send again
              </button>
          </PageAlert>
          <PageAlert show={invoice.status === 'delivery_failed'} tone="red" title="The send to QuickBooks failed">
              {invoice.delivery_error || 'No error text was recorded.'}{' '}
              <button
                onClick={deliverNow}
                disabled={busy}
                data-testid="deliver-retry"
                className="underline text-red-300 hover:text-red-200 disabled:opacity-50"
              >
                Try again
              </button>
          </PageAlert>
          <PageAlert show={deliveryOwed} tone="gold" title="The forward to QuickBooks is owed">
              Confirmed with sending on, and not yet delivered. The sweep retries it, or{' '}
              <button
                onClick={deliverNow}
                disabled={busy}
                data-testid="deliver-now"
                className="underline text-yellow-200 hover:text-yellow-100 disabled:opacity-50"
              >
                send it now
              </button>.
          </PageAlert>

          {/* Tax has its own cell because it is a header fact, not a line: it
              was read, stored and printed on the forwarded summary while this
              screen showed only Subtotal and Total, so a bookkeeper checking a
              DigiKey bill could find no sales tax anywhere on the page. */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
            <Field label="Invoice date">{invoice.invoice_date ? formatEventDate(invoice.invoice_date) : '—'}</Field>
            <Field label="Due">
              {invoice.due_date ? (
                <span className={isOverdue(invoice) ? 'text-red-300' : ''}>
                  {formatEventDate(invoice.due_date)}
                </span>
              ) : '—'}
            </Field>
            <Field label="Subtotal">{formatCents(invoice.subtotal_cents)}</Field>
            <Field label="Tax">{formatCents(invoice.tax_cents)}</Field>
            <Field label="Total">{formatCents(invoice.total_cents)}</Field>
            {invoice.payment_terms && <Field label="Terms">{invoice.payment_terms}</Field>}
          </div>

          {/* WHAT THIS IS FOR, so nobody mistakes it for an integration.
              QuickBooks reads four things off a forwarded document: dates,
              amounts, vendor, and a card's last four. It cannot receive a class
              or an account that way and never will over mail. These are picked
              here so they PRINT on the summary, and the person entering the
              bill reads them instead of deciding twice. Neither gates confirm. */}
          {options && (
            <Panel padding="sm" label="Classification" className="mb-4">
              <div className="flex items-baseline justify-between mb-2">
                <div className="font-mono text-[10px] uppercase tracking-widest text-on-surface-variant">
                  Classification
                </div>
                <span className="text-[11px] text-on-surface-variant">
                  Prints on the summary. QuickBooks cannot read it off the page.
                </span>
              </div>

              <div className="font-mono text-[10px] uppercase tracking-widest text-on-surface-variant mb-1">
                Bought for
              </div>
              <ChipGroup label="Bought for" className="mb-3">
                {options.classes.map((c) => {
                  const on = invoice.purchase_class_id === c.id;
                  return (
                    <Chip
                      key={c.id}
                      pressed={on}
                      disabled={!inReview || busy}
                      onClick={() => setClassification({ purchase_class_id: on ? null : c.id })}
                    >
                      {c.label}
                    </Chip>
                  );
                })}
              </ChipGroup>

              {/* The chart of accounts runs to seventy-odd rows once the real
                  one is seeded, so this is the shared typeahead rather than a
                  select nobody can scan. The account NUMBER is the chip, because
                  that is what gets typed into QuickBooks, and chips are
                  searchable: "5400" finds it as readily as "drones" does. */}
              <EntityCombobox
                id="invoice-account"
                label="Account"
                labelClassName="font-mono text-[10px] uppercase tracking-widest text-on-surface-variant mb-1 block"
                inputClassName="m-0 w-full bg-surface-container border border-outline-variant rounded px-2 py-1.5 text-sm text-on-surface"
                value={invoice.expense_category_id ?? ''}
                options={categoryOptions}
                disabled={!inReview || busy}
                placeholder="Search the chart of accounts"
                emptyMessage="No account matches that."
                noOptionsMessage="No accounts yet. They come from the QuickBooks chart of accounts."
                onSelect={(o) => setClassification({
                  expense_category_id: o ? String(o.id) : null,
                })}
              />

              {inReview && (
                <Checkbox
                  size="sm"
                  className="mt-2"
                  checked={applyToAll}
                  onChange={(e) => setApplyToAll(e.target.checked)}
                  label={`Apply to all ${lines.length} line${lines.length === 1 ? '' : 's'}`}
                />
              )}
            </Panel>
          )}

          {/* Everything the document NAMED, which the summary also carries. Here
              it is read; there it is typed from. Bill-to and ship-to genuinely
              differ on real bills (one Digikey invoice bills Firefly Swarm and
              ships to Firefly Drone Shows), and a total cannot tell you that. */}
          {(() => {
            const parties = invoice.parties_observed || {};
            const blocks: [string, string[]][] = [
              ['Bill to', partyLines(parties.bill_to)],
              ['Ship to', partyLines(parties.ship_to)],
              ['Ship from', partyLines(parties.ship_from)],
            ].filter(([, l]) => l.length) as [string, string[]][];
            const buyer = partyLines(parties.buyer).join(', ');
            if (!blocks.length && !buyer && !invoice.order_source) return null;
            return (
              <Panel padding="sm" label="Named on this document" className="mb-4">
                <div className="font-mono text-[10px] uppercase tracking-widest text-on-surface-variant mb-2">
                  Named on this document
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {blocks.map(([label, lines]) => (
                    <div key={label}>
                      <div className="font-mono text-[10px] uppercase tracking-widest text-on-surface-variant mb-0.5">
                        {label}
                      </div>
                      <div className="text-[13px] text-on-surface whitespace-pre-line">{lines.join('\n')}</div>
                    </div>
                  ))}
                </div>
                {(buyer || invoice.order_source) && (
                  <div className="flex flex-wrap gap-x-6 gap-y-1 mt-2 text-[13px] text-on-surface-variant">
                    {buyer && <span>Buyer: <span className="text-on-surface">{buyer}</span></span>}
                    {invoice.order_source && (
                      <span>Ordered via: <span className="text-on-surface">{invoice.order_source}</span></span>
                    )}
                  </div>
                )}
              </Panel>
            );
          })()}

          {/* WHERE THE VENDOR ASKED TO BE PAID, in full. The forwarded page
              carries the name and address only: QuickBooks reads four things off
              an emailed document and remittance is not among them, so an account
              number there would sit in Intuit's store doing no work. A change to
              these between invoices raises its own banner above. */}
          {invoice.remit_to_observed && (
            <Panel padding="sm" label="Remit to" className="mb-4">
              <div className="font-mono text-[10px] uppercase tracking-widest text-on-surface-variant mb-1">
                Remit to
              </div>
              <div className="text-[13px] text-on-surface whitespace-pre-line">
                {[invoice.remit_to_observed.name, ...addressLines(invoice.remit_to_observed.address)]
                  .filter(Boolean).join('\n')}
              </div>
              {(invoice.remit_to_observed.bank_name || invoice.remit_to_observed.account_number
                || invoice.remit_to_observed.routing_number || invoice.remit_to_observed.swift) && (
                <dl className="mt-2 grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-[13px]">
                  {([
                    ['Bank', invoice.remit_to_observed.bank_name],
                    ['Account', invoice.remit_to_observed.account_number],
                    ['Routing', invoice.remit_to_observed.routing_number],
                    ['SWIFT', invoice.remit_to_observed.swift],
                  ] as [string, string | null | undefined][]).filter(([, v]) => v).map(([label, value]) => (
                    <React.Fragment key={label}>
                      <dt className="text-on-surface-variant">{label}</dt>
                      <dd className="font-mono text-on-surface">{value}</dd>
                    </React.Fragment>
                  ))}
                </dl>
              )}
              <p className="mt-2 text-[11px] text-on-surface-variant">
                Bank details stay on this screen. The page that forwards carries the name and
                address only.
              </p>
            </Panel>
          )}

          {/* The address this document printed. It wins over the vendor record
              on the summary, because the invoice is the more recent evidence —
              and where the record has none, one click puts it there. */}
          {addressLines(invoice.vendor_address_observed).length > 0 && (
            <Panel padding="sm" label="Vendor address" className="mb-4">
              <div className="font-mono text-[10px] uppercase tracking-widest text-on-surface-variant mb-1">
                Vendor address, off this invoice
              </div>
              <div className="text-sm text-on-surface whitespace-pre-line">
                {addressLines(invoice.vendor_address_observed).join('\n')}
              </div>
              {invoice.vendor_id && !invoice.vendor_has_address && (
                <button
                  type="button"
                  onClick={saveVendorAddress}
                  disabled={busy}
                  data-testid="adopt-vendor-address"
                  className={`${buttonClass('quiet', 'sm')} mt-2`}
                >
                  Save it to {invoice.vendor_name || 'this vendor'}&apos;s record
                </button>
              )}
            </Panel>
          )}

          {/* The bookkeeper's note, on confirmed records only (the server holds
              the same line). Open / Partial / Paid is HER note — the actual
              payment happens in QuickBooks; this is where she tracks it without
              keeping QBO open in a second window. Due date is editable here
              because a reader that missed it otherwise leaves a blank nobody
              can fix in-app. */}
          {['confirmed', 'delivered', 'delivery_failed'].includes(invoice.status) && (
            <Panel padding="sm" label="Payment" className="mb-4">
              <div className="font-mono text-[10px] uppercase tracking-widest text-on-surface-variant mb-2">
                Payment
              </div>
              {/* key: the route keeps this component mounted across /invoices/A
                  → /invoices/B, and a mutating control must not carry A's
                  drafts (arming, dueDraft) into B's window. */}
              <PaymentNote
                key={invoice.id}
                invoice={invoice}
                showDueDate
                onSaved={(updated) =>
                  setDetail((d) => (d ? { ...d, invoice: { ...d.invoice, ...updated } } : d))
                }
              />
            </Panel>
          )}

          <div className="flex items-baseline gap-2 mb-2">
            <h2 className="text-sm font-medium text-on-surface">Lines</h2>
            <span className="text-[12px] text-on-surface-variant">
              {lines.length} on this invoice
              {orderChoices.length ? `, against ${orderChoices.map((o) => o.po_number).join(', ')}` : ''}
            </span>
          </div>

          <InvoiceLinesTable
            lines={lines}
            inReview={inReview}
            proposalByLine={proposalByLine}
            onPick={pick}
            busy={busy}
          />

          {inReview && (
            <>
            <Checkbox
              className="mt-5"
              data-testid="send-toggle"
              checked={sendToBooks}
              disabled={totalless}
              onChange={(e) => setSendChoice(e.target.checked)}
              label={totalless
                ? 'Sending needs a readable USD total; fix the total to forward this one'
                : sendingLive
                  ? 'Send to QuickBooks on confirm'
                  : 'Queue the forward to QuickBooks'}
              description={<span className="font-mono text-[11px]">{detail.qbo_forward_address}</span>}
            />
            {/* OUTSIDE the label on purpose: text inside a wrapping label joins
                the checkbox's accessible name, and a screen reader should hear
                "Queue the forward to QuickBooks", not a paragraph. */}
            <Reveal show={!totalless && !sendingLive}>
              <p className="mt-1 ml-6 text-xs text-on-surface-variant max-w-prose">
                Sending is switched off in this environment, so nothing goes out yet. Leave this
                on and the forward happens on the first sweep after sending is switched on.
                Switching it off is a decision that stands: the sweep never picks those up.
              </p>
            </Reveal>
            </>
          )}
          <div className="flex items-center gap-3 mt-3 pt-4 border-t border-outline-variant">
            <Tooltip label={vendorUnknown ? 'Held: pick or create the vendor first.' : ''}>
              <button
                disabled={!inReview || vendorUnknown || busy}
                onClick={confirm}
                aria-describedby={vendorUnknown ? 'confirm-held-reason' : undefined}
                className={buttonClass('filled')}
              >
                Confirm
              </button>
            </Tooltip>
            {/* The page that forwards, before it forwards. Stamped DRAFT until
                this invoice is confirmed, so a saved copy of a preview is never
                mistaken for the document that went to the books. */}
            <button
              type="button"
              onClick={openSummaryPdf}
              disabled={openingSummary}
              className={buttonClass('quiet')}
            >
              {openingSummary ? 'Opening…' : 'Summary PDF'}
            </button>
            {inReview && !reasonMode && (
              <button
                onClick={() => setReasonMode('discard')}
                className={buttonClass('quiet')}
              >
                Discard
              </button>
            )}
            {['confirmed', 'delivered', 'delivery_failed'].includes(invoice.status) && !reasonMode && (
              <button
                onClick={() => setReasonMode('void')}
                className={buttonClass('danger')}
              >
                Void
              </button>
            )}
            <span id="confirm-held-reason" className="font-mono text-[11px] text-on-surface-variant">
              {vendorUnknown
                ? 'Confirm is held: unknown vendor.'
                : inReview
                  ? (sendToBooks
                    ? 'Confirming rolls billing onto the order, then forwards the summary.'
                    : 'Confirming rolls billing onto the order. Nothing goes to QuickBooks.')
                  : invoice.status === 'confirmed' && !invoice.delivery_intended
                    ? 'Confirmed, kept out of QuickBooks by choice.'
                    : `This invoice is ${invoice.status}.`}
            </span>
          </div>

          <Reveal show={!!reasonMode}>
            <Panel padding="sm" label="Reason" className="mt-3">
              <div className="text-sm text-on-surface mb-2">
                {reasonMode === 'void'
                  ? 'Why is this bill being voided? The order\u2019s feed carries this answer.'
                  : 'Why is this leaving the pile? The next person reads this.'}
              </div>
              <TextArea
                aria-label={reasonMode === 'void' ? 'Why this bill is being voided' : 'Why this invoice is leaving the pile'}
                value={reasonText}
                onChange={(e) => setReasonText(e.target.value)}
                className="mb-2"
                rows={2}
                data-testid="discard-reason"
                placeholder={reasonMode === 'void'
                  ? 'Vendor reissued the bill / billed the wrong order / disputed and dropped…'
                  : 'Duplicate of 129899112 / not ours / handled in receipts…'}
              />
              <div className="flex gap-2">
                <button
                  onClick={submitReason}
                  disabled={busy || !reasonText.trim()}
                  className={buttonClass('danger')}
                >
                  {reasonMode === 'void' ? 'Void, on record' : 'Discard, on record'}
                </button>
                <button onClick={() => setReasonMode(null)} className={buttonClass('quiet')}>
                  Keep it
                </button>
              </div>
            </Panel>
          </Reveal>
        </div>

        {/* Below lg the paper comes FIRST: stacked under the confirm button it
            defeated the page's own control ("never confirm without the paper").
            CSS order, so the desktop DOM and tab order are untouched.

            A HAND-TYPED MEMO HAS NO PAPER. `source: 'manual'` is the credit note
            somebody recorded off a purchase return; the file route 404s for it,
            and a viewer that can only ever fail is worse than a sentence saying
            why there is nothing to show. The reading beside it is not a proposal
            either: a person typed it, so the pane's usual warning would be
            telling them to check their own work against a document that does not
            exist. */}
        {!hasPaper && !selectedOrderId ? (
          <div className="order-first lg:order-none">
            <p className="text-sm text-on-surface-variant">
              Recorded from the vendor&apos;s credit note. No file.
            </p>
          </div>
        ) : (
          /* The height lives on this wrapper and the pane fills it, rather than
             both carrying a height utility and resolving by stylesheet order. */
          <div className="order-first lg:order-none h-[620px] lg:sticky lg:top-4 lg:h-[calc(100vh-6rem)] flex flex-col gap-2">
            <div className="shrink-0 flex items-center gap-3 min-w-0">
              <Segmented
                label="Show beside the reading"
                value={tab}
                onChange={(k) => {
                  setOrderSeen(true);
                  setPaneTab(k as PaneTab);
                }}
                options={[
                  { key: 'invoice', label: 'Invoice' },
                  selectedOrderId
                    ? { key: 'order', label: 'Order' }
                    : { key: 'order', label: 'Order', disabled: true, title: 'No order matched yet' },
                ]}
              />
              {tab === 'order' && orderChoices.length > 1 && selectedOrderId && (
                <FilterSelect
                  label="Which order"
                  hideLabel
                  value={selectedOrderId}
                  onChange={setOrderPick}
                  className="ml-auto"
                >
                  {orderChoices.map((o) => <option key={o.id} value={o.id}>{o.po_number}</option>)}
                </FilterSelect>
              )}
            </div>
            {/* Hidden, never unmounted: the pane owns the fetched blob, and an
                unmount would fetch the whole document again on the way back. */}
            <div data-testid="invoice-paper" className={tab === 'invoice' ? 'flex-1 min-h-0' : 'hidden'}>
              {hasPaper ? (
                <DocumentPane
                  fetchBlob={() => api.fileBlob(invoiceId)}
                  refreshKey={invoiceId}
                  mimeType={invoice.mime_type}
                  emptyCopy="The reading on the left is a proposal; never confirm without the paper."
                  fill
                />
              ) : (
                <p className="text-sm text-on-surface-variant">
                  Recorded from the vendor&apos;s credit note. No file.
                </p>
              )}
            </div>
            {selectedOrderId && (tab === 'order' || orderSeen) && (
              <div className={tab === 'order' ? 'flex-1 min-h-0' : 'hidden'}>
                <PoReadOnlyPane purchaseOrderId={selectedOrderId} highlightLineIds={highlightLineIds} />
              </div>
            )}
          </div>
        )}
      </div>
    </PageLayout>
  );
}
